/**
 * SCENE 09: THE IMPLOSION OF SOUND (Cyberpunk // 2077 AD)
 * High-speed neon data storm implodes into a single white dot followed by 0dB absolute silence.
 */

import { ISceneLifecycle } from './ISceneLifecycle';
import { CameraState } from '../renderer/CameraSpline';
import { EventBus, EventType } from '../core/EventBus';
import { StateStore, StateBitmask } from '../state/Store';

export class Scene09_CyberImplosion implements ISceneLifecycle {
  public id = 8;
  public name = 'The Implosion of Sound';
  public era = 'Digital Peak (2077 AD)';

  private eventBus: EventBus;
  private store: StateStore;
  private holdTimer: number = 0;
  private isImploded: boolean = false;
  private implosionScale: number = 1.0;
  private unsubscribeList: Array<() => void> = [];

  constructor() {
    this.eventBus = EventBus.getInstance();
    this.store = StateStore.getInstance();
  }

  public async preload(): Promise<void> { return Promise.resolve(); }
  public mount(): void {}

  public activate(): void {
    const unsubDown = this.eventBus.subscribe(EventType.INPUT_POINTER_DOWN, () => {
      this.holdTimer = 0;
    });

    const unsubMove = this.eventBus.subscribe(EventType.INPUT_POINTER_MOVE, () => {
      if (!this.isImploded) {
        this.holdTimer += 0.1;
      }
    });

    this.unsubscribeList.push(unsubDown, unsubMove);
  }

  public update(dt: number): void {
    if (this.holdTimer > 1.2 && !this.isImploded) {
      this.isImploded = true;
      this.store.setFlag(StateBitmask.CYBER_IMPLODED, true);
      this.eventBus.emit(EventType.AUDIO_TRIGGER_IMPLOSION);
      this.eventBus.emit(
        EventType.ACCESSIBILITY_ANNOUNCE,
        0, 0, 0, 0, 0, 1, 8,
        'Universe violently implodes into a single white dot! All audio drops to absolute 0dB silence.'
      );
    }

    if (this.isImploded && this.implosionScale > 0.001) {
      this.implosionScale = Math.max(0.001, this.implosionScale - dt * 2.5);
    }
  }

  public render(ctx: CanvasRenderingContext2D, width: number, height: number, _camera: CameraState): void {
    ctx.save();
    const cx = width / 2;
    const cy = height / 2;

    if (this.isImploded) {
      if (this.implosionScale > 0.05) {
        // Imploding neon box shrinking to a single dot
        ctx.fillStyle = '#020408';
        ctx.fillRect(0, 0, width, height);

        const wSize = width * this.implosionScale;
        const hSize = height * this.implosionScale;

        ctx.fillStyle = '#00f0ff';
        ctx.fillRect(cx - wSize / 2, cy - hSize / 2, wSize, hSize);
      } else {
        // Pure White Light Space after 0dB silence
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, width, height);

        ctx.font = '600 13px "Inter", sans-serif';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#0d0b0a';
        ctx.fillText('0dB SILENCE  //  THE PRIMORDIAL AWAKENING', cx, cy);
      }
    } else {
      // Neon Data Storm Rain
      ctx.fillStyle = '#050714';
      ctx.fillRect(0, 0, width, height);

      ctx.fillStyle = 'rgba(0, 240, 255, 0.7)';
      for (let i = 0; i < 40; i++) {
        const rx = (Math.sin(i * 47) * 0.5 + 0.5) * width;
        const ry = (Math.cos(i * 19 + performance.now() * 0.005) * 0.5 + 0.5) * height;
        ctx.fillRect(rx, ry, 2, 25);
      }

      ctx.font = '400 11px monospace';
      ctx.textAlign = 'center';
      ctx.fillStyle = 'rgba(0, 240, 255, 0.6)';
      ctx.fillText('[ PRESS AND HOLD SCREEN TO STABILIZE DATA STORM ]', cx, height - 40);
    }

    ctx.restore();
  }

  public deactivate(): void {
    this.unsubscribeList.forEach(fn => fn());
    this.unsubscribeList = [];
  }
  public unmount(): void {}
  public dispose(): void {}
}
